/**
 * The editable colour bar, and the scale bar that goes on every 3D view.
 *
 * Both are controls, not decoration. A colour bar with no numbers on it says
 * only that some cells differ from others; a map with no scale bar says
 * nothing about how far apart two wells are, which on a field that spans three
 * hundred kilometres is the first thing anyone needs to know.
 */
import { EditColoursButton } from "./PaletteEditor";
import { Button, Dropdown, OptionalNumberField, Toggle } from "./ui";
import {
  formatValue as fmt,
  makePainter,
  rampGradient,
  rampKeys,
  rampLabel,
  type ColorScale,
} from "../lib/colorscale";

/** The bar itself: a gradient strip with its two end values. */
export function ColorBar({
  property,
  scale,
  dataMin,
  dataMax,
  compact,
  minWidth,
}: {
  property: string;
  scale: ColorScale;
  dataMin: number;
  dataMax: number;
  compact?: boolean;
  /** Lebar terkecil, menggantikan bawaannya.
   *
   *  Nol dipakai di kepala track lembar log, tempat barnya harus menyusut
   *  bersama tracknya: sebuah lebar terkecil 150 piksel di kolom selebar 90
   *  mendorong kepala track keluar dari lembarnya. */
  minWidth?: number;
}) {
  const painter = makePainter(scale, dataMin, dataMax);
  const steps = Array.from({ length: 28 }, (_, i) => i / 27);
  const clamped = scale.min !== null || scale.max !== null;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        minWidth: minWidth ?? (compact ? 150 : 200),
      }}
    >
      <div style={{ display: "flex", height: compact ? 10 : 13, borderRadius: 3, overflow: "hidden" }}>
        {steps.map((t) => (
          <div key={t} style={{ flex: 1, background: painter.atFraction(t) }} />
        ))}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          font: "400 9px var(--font-mono)",
          color: "var(--ink-6)",
        }}
      >
        <span>{fmt(painter.lo)}</span>
        <span style={{ color: "var(--ink-4)" }}>
          {property}
          {/* Says whether the ends are the data's own or someone's choice. A
              stretched view and a clamped one look identical otherwise. */}
          {clamped ? " · clamped" : ""}
        </span>
        <span>{fmt(painter.hi)}</span>
      </div>
    </div>
  );
}

/**
 * The panel that edits it: ramp, its colours, direction, and the two ends.
 *
 * The ends are typed, not dragged. A slider across the data range cannot land
 * on 0.10 porosity or 10 mD — the numbers a cutoff is actually stated in — and
 * cannot reach outside the data at all, which is exactly what someone matching
 * the scale of a printed sheet from another field needs. Left blank, an end
 * follows the data again.
 */
export function ColorBarEditor({
  property,
  scale,
  onChange,
  dataMin,
  dataMax,
}: {
  property: string;
  scale: ColorScale;
  onChange: (next: ColorScale) => void;
  dataMin: number;
  dataMax: number;
}) {
  const set = (patch: Partial<ColorScale>) => onChange({ ...scale, ...patch });
  const span = Math.abs(dataMax - dataMin);
  const step = span > 0 ? Number((span / 100).toPrecision(1)) : 0.01;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <ColorBar property={property} scale={scale} dataMin={dataMin} dataMax={dataMax} />
      <Dropdown
        label="Colour ramp"
        value={scale.ramp}
        onChange={(value) => set({ ramp: value })}
        options={rampKeys().map((key) => ({
          value: key,
          label: rampLabel(key),
        }))}
      />
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {rampKeys().map((key) => (
          <button
            key={key}
            type="button"
            aria-label={rampLabel(key)}
            onClick={() => set({ ramp: key })}
            style={{
              width: 34,
              height: 13,
              padding: 0,
              cursor: "pointer",
              borderRadius: 3,
              border:
                key === scale.ramp ? "2px solid var(--ink)" : "1px solid var(--border-control)",
              background: rampGradient(key),
            }}
          />
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
        <Toggle label="Reverse" checked={scale.reverse} onChange={(v) => set({ reverse: v })} />
        <EditColoursButton ramp={scale.ramp} onRamp={(ramp) => set({ ramp })} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <OptionalNumberField
          label="Minimum"
          value={scale.min}
          step={step}
          placeholder={fmt(dataMin)}
          onChange={(min) => set({ min })}
        />
        <OptionalNumberField
          label="Maximum"
          value={scale.max}
          step={step}
          placeholder={fmt(dataMax)}
          onChange={(max) => set({ max })}
        />
      </div>
      {(scale.min !== null || scale.max !== null) && (
        <Button variant="ghost" onClick={() => set({ min: null, max: null })}>
          Back to the data range
        </Button>
      )}
    </div>
  );
}

/**
 * A scale bar drawn over a view, with the length either chosen or automatic.
 *
 * Rendered as an SVG group so it can sit inside the same viewBox as the slice
 * and stay put while the slice is redrawn.
 */
export function ScaleBarSvg({
  pixels,
  label,
  x,
  y,
  ink = "var(--ink-4)",
  k = 1,
}: {
  pixels: number;
  label: string;
  x: number;
  y: number;
  ink?: string;
  /**
   * Pengali ukuran layar untuk peta yang zoom-nya menggeser `viewBox`.
   *
   * Panjang batangnya tidak ikut: `pixels` adalah jarak sungguhan, dan dua
   * kilometer memang harus menempati dua kali lebih banyak piksel ketika peta
   * diperbesar dua kali. Yang dikembalikan ke ukuran semula hanyalah garis,
   * kotak, dan tulisannya — batang skala setinggi tiga sentimeter mengukur
   * hal yang sama dengan yang setinggi lima milimeter, hanya lebih sulit
   * dilihat menembusnya.
   */
  k?: number;
}) {
  return (
    <g>
      <rect x={x - 4 * k} y={y - 16 * k} width={pixels + 8 * k} height={24 * k}
            fill="var(--panel)" opacity={0.72} />
      <line x1={x} x2={x + pixels} y1={y} y2={y} stroke={ink} strokeWidth={2 * k} />
      <line x1={x} x2={x} y1={y - 5 * k} y2={y + 5 * k} stroke={ink} strokeWidth={2 * k} />
      <line x1={x + pixels} x2={x + pixels} y1={y - 5 * k} y2={y + 5 * k}
            stroke={ink} strokeWidth={2 * k} />
      <text x={x + pixels / 2} y={y - 7 * k} textAnchor="middle"
            style={{ font: `600 ${(11 * k).toFixed(2)}px var(--font-mono)`, fill: ink }}>
        {label}
      </text>
    </g>
  );
}
